import { useEffect, useRef, useState } from "react";

import { isKeyOf } from "@smart/itops-utils-basic";

import { fileTypes } from "./file-types";
import { UploadFileProps, UploadFn, UploadStatus } from "./types";
import { abortMessage } from "./upload-file";
import { Button } from "../../button";
import { Icon } from "../../icon";

export type FileFieldItemProps = {
  fileName: string;
  downloadUrl: string | undefined;
  uploadUrl: string | undefined;
  file: File | undefined;
  uploadStatus: UploadStatus;
  upload: UploadFn;
  onStatusChange: (uploadStatus: UploadStatus) => void;
  onRemove: () => void;
  uploadFileProps: UploadFileProps;
  fileSizeLimitMegaByte: number;
  maxNumOfFiles: number;
  fileIndex: number;
  fieldId: string;
};

const knownFileType = isKeyOf(fileTypes);
const fileNameToIcon = (name: string) => {
  const extension = name.split(".").reverse()[0];
  return knownFileType(extension) ? fileTypes[extension] : fileTypes.txt;
};

export const FileFieldItem = ({
  fileName,
  downloadUrl,
  uploadUrl,
  file,
  uploadStatus,
  upload,
  onStatusChange,
  onRemove,
  fileSizeLimitMegaByte,
  maxNumOfFiles,
  fileIndex,
  fieldId: id,
  uploadFileProps: {
    onProgress,
    onUploadStatusChange,
    initialise,
    getUploadStates,
  },
}: FileFieldItemProps) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortUploadRef = useRef<(() => void) | null>(null);
  const uploadStates = getUploadStates(id);
  const notUploadedStatuses: UploadStatus[] = [
    "failedToUpload",
    "exceedSizeLimit",
    "unsupportedFileType",
    "fileEncrypted",
  ];
  const failedUploadReason: Partial<Record<UploadStatus, string>> = {
    exceedSizeLimit: `File exceeds the ${fileSizeLimitMegaByte} MB limit. Please upload a smaller file and try again.`,
    unsupportedFileType:
      "Unsupported file format. Please upload a valid file type.",
    failedToUpload: "Upload failed. Please try again.",
    fileEncrypted: "Cannot upload a password protected file.",
  };

  const extractFileDetails = () => {
    const parts = fileName.split(".");
    const extension = parts.pop();

    return {
      name: parts.join("."),
      extension: `.${extension}`,
    };
  };

  const { name: namePart, extension } = extractFileDetails();

  useEffect(() => {
    if (uploadStates.length > 0) {
      setUploading(uploadStates[fileIndex].uploading);
      setProgress(uploadStates[fileIndex].progress);
    } else {
      initialise(id, maxNumOfFiles);
    }

    if (progress === 100 && uploadStatus === "uploading") {
      onUploadStatusChange(id, fileIndex, false);
      onStatusChange("uploaded");
    }
  }, [uploadStates, progress, uploading]);

  useEffect(() => {
    if (uploadStatus === "notUploaded" && uploadUrl && file && !uploading) {
      onUploadStatusChange(id, fileIndex, true);
      onStatusChange("uploading");
      setUploading(true);

      const { promise, abort } = upload({
        uploadUrl,
        file,
        onProgress: (newProgress) => {
          onProgress(id, fileIndex, newProgress);
          setProgress(newProgress);
        },
      });

      abortUploadRef.current = abort;

      promise
        .then(() => {
          onUploadStatusChange(id, fileIndex, false);
          onStatusChange("uploaded");
          setUploading(false);
          abortUploadRef.current = null;
        })
        .catch((e) => {
          if (e.message !== abortMessage) {
            console.error(e);
            onUploadStatusChange(id, fileIndex, false);
            onStatusChange("failedToUpload");
          }
          setUploading(false);
          abortUploadRef.current = null;
        });
    }
  }, [uploadStatus, uploadUrl]);

  const errorMessage = failedUploadReason[uploadStatus];
  const getFileStatus = () => {
    if (uploading) {
      return (
        <div
          className="mt-[0.6rem] mb-[0.4rem] ml-2 h-[0.8rem] bg-neutral-200 rounded-small overflow-hidden"
          role="progressbar"
          aria-label="Upload Progress"
          aria-valuenow={progress}
        >
          <div
            className="h-full bg-blue-460 transition-all duration-200 ease-in-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      );
    }

    if (errorMessage)
      return (
        <div className="ml-[0.5rem] mt-[0.6rem] flex items-center gap-small whitespace-break-spaces text-red-550 text-caption font-semibold">
          <Icon name="regularCircleExclamation" className="w-[1rem] h-[1rem]" />
          <div>{errorMessage}</div>
        </div>
      );

    return null;
  };

  return (
    <div className="bg-neutral-50 flex flex-row flex-nowrap justify-between items-center gap-small m-0 px-[1.6rem] py-[0.4rem] rounded text-caption">
      <div className="flex flex-col overflow-hidden flex-1">
        <div className="flex flex-row flex-nowrap items-center gap-small w-full">
          <div className="w-[2rem] h-[2rem]">
            <img src={fileNameToIcon(fileName)} alt={fileName} />
          </div>
          <p className="flex m-0 relative text-ui overflow-hidden">
            {uploading ||
            !downloadUrl ||
            notUploadedStatuses.includes(uploadStatus) ? (
              <span className="block whitespace-nowrap overflow-hidden overflow-ellipsis">
                {namePart}
                {extension}
              </span>
            ) : (
              <a
                className="overflow-hidden"
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={fileName}
              >
                <span className="block whitespace-nowrap overflow-hidden overflow-ellipsis">
                  {namePart}
                  {extension}
                </span>
              </a>
            )}
          </p>
        </div>
        {getFileStatus()}
      </div>
      <div className="flex justify-end gap-small max-w-full ml-[0.8rem]">
        {uploadStatus === "uploaded" && !errorMessage && (
          <div className="flex items-center text-green-600">
            <Icon name="regularCheckCircle" />
          </div>
        )}
        <Button
          variant="secondarySubtle"
          leftIcon="regularXmarkLarge"
          size="small"
          onClick={() => {
            if (abortUploadRef.current) {
              abortUploadRef.current();
              setUploading(false);
              onUploadStatusChange(id, fileIndex, false);
            }
            onRemove();
          }}
          title="Remove file"
        />
      </div>
    </div>
  );
};
